import React from 'react';
import readingTime from 'reading-time';
import { TFunction } from 'i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import get from 'lodash/get';
import MediaService, { fallbackToLargestFile } from '../../utils/DeviationMediaService';
import { getDeviationType } from '../_helpers';
import { DeviationTypes } from '../../types/deviation';
import { getFeature } from '@wix/da-editor-tiptap/pkg/server/features/helpers';

type CoverSourceAndTarget = {
  deviation: PapiDeviation;
  width?: number;
  height?: number;
  allowHuge?: boolean;
};

function coverDeviationExists(
  source: Partial<CoverSourceAndTarget>
): source is CoverSourceAndTarget {
  return !!source.deviation;
}

// todo: maybe possible to move/remove after cover_attachment
export const getCoverImageAttributes = (source: Partial<CoverSourceAndTarget>) => {
  const preview = getCoverImageFile(source);
  if (!preview) {
    return;
  }
  const style: React.CSSProperties = {
    backgroundImage: `url('${preview.src}')`,
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: 'center',
    backgroundPositionY: 'center',
    backgroundSize: 'cover',
  };
  return { style };
};

// todo: maybe possible to move/remove after cover_attachment
export function getCoverImageFile(source: Partial<CoverSourceAndTarget>) {
  if (!coverDeviationExists(source)) {
    return;
  }

  if (source.deviation && source.deviation.coverImage) {
    return getCoverImageWithDraftJSType(source);
  }

  return get(source.deviation, 'textContent.html.type') === 'writer'
    ? getCoverImageWithWriterType(source)
    : getCoverImageWithDraftJSType(source);
}

function getDraftJSCoverImage({ deviation }: CoverSourceAndTarget) {
  // cover image can either be inside the coverImage property or
  // if it's not a text deviation it's the passed deviation itself

  const { coverImage: sourceCoverImage, textContent: sourceTextContent } = deviation;

  if (sourceCoverImage) {
    return sourceCoverImage;
  }

  if (!sourceTextContent) {
    return deviation;
  }
}

/**
 * DraftJS has two versions:
 * - Legacy first image was used in beta stages
 * - CoverImage was when we let users select the cover
 */
function getCoverImageWithDraftJSType(source: CoverSourceAndTarget) {
  // Prefer coverImage over firstImage
  const deviation = getDraftJSCoverImage(source);

  if (!deviation) {
    return;
  }

  // Finally we try and find a image that will cover the requested block size
  return getCoverImageForSource({ ...source, deviation });
}

function getCoverImageWithWriterType(source: CoverSourceAndTarget) {
  if (getDeviationType(source.deviation) === DeviationTypes.STATUS) {
    const deviation = source.deviation.status?.items?.find(
      item => item.type === 'thumb_background_deviation'
    )?.deviation;

    return deviation ? getCoverImageForSource({ ...source, deviation }) : undefined;
  }

  return getCoverImageForSource(source);
}

function getCoverImageForSource({
  deviation,
  width = 300,
  height = 300,
  allowHuge,
}: CoverSourceAndTarget) {
  if (deviation.isBlocked) {
    return;
  }

  return MediaService.getSmallestThumbThatFullyCovers(
    deviation,
    width,
    height,
    fallbackToLargestFile,
    {
      allowPreview: allowHuge,
      allowFullview: allowHuge,
    }
  );
}

export const getReadingTimeMinutes = (textContent?: PapiDeviation['textContent']) => {
  if (!textContent) {
    return 0;
  }
  if (!isTextContentDraftJs(textContent)) {
    if (!textContent.html.markup) {
      return 0;
    }

    // Wrong but meh...
    return Math.ceil(readingTime(textContent.html.markup).minutes);
  }

  const counts = getFeature('WORD_COUNT_FEATURE', textContent.html.features);
  if (!counts) {
    return 0;
  }
  return Math.ceil(counts.data.words / 200);
};

export const isTextContentDraftJs = (textContent?: PapiDeviation['textContent']): boolean =>
  !!(textContent && textContent.html && textContent.html.type === 'draft');

export const textContentHasMedia = (textContent: PapiDeviation['textContent']): boolean => {
  if (!isTextContentDraftJs(textContent)) {
    return false;
  }
  const mediaCountFeature = getFeature('MEDIA_COUNT_FEATURE', textContent!.html.features);
  const mediaCounts = get(mediaCountFeature, 'data', {});
  return mediaCounts && (mediaCounts['images'] > 0 || mediaCounts['video'] > 0);
};

export function getTranslationKeyForAccessibleDeviationLink(
  t: TFunction,
  deviation: PapiDeviation
) {
  const type = getDeviationType(deviation);
  const args = {
    title: deviation.title,
    author: deviation.author?.username,
  };
  switch (type) {
    case DeviationTypes.IMAGE:
      return t('deviationview.a11y.link.visualArt', args);
    case DeviationTypes.JOURNAL:
      return t('deviationview.a11y.link.journal', args);
    case DeviationTypes.LITERATURE:
      return t('deviationview.a11y.link.literature', args);
    case DeviationTypes.FILM:
      return t('deviationview.a11y.link.film', args);
    case DeviationTypes.PDF:
      return t('deviationview.a11y.link.pdf', args);
    case DeviationTypes.POLL:
      return t('deviationview.a11y.link.poll', args);
    case DeviationTypes.COMMISSION:
      return t('deviationview.a11y.link.commission', args);
    case DeviationTypes.STATUS:
      return t('deviationview.a11y.link.status', args);
    case DeviationTypes.TIER:
      return t('deviationview.a11y.link.tier', args);
    case DeviationTypes.UNKNOWN:
    default:
      return t('deviationview.a11y.link.unknown', args);
  }
}
